import React, { ChangeEvent, useMemo, useRef, useState } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { ErrorMessage } from '../../../../../../base/components/Forms/ErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../../../../base/hooks';
import { __t } from '../../../../../../base/utils/t';
import { Icon } from '../../../../../../base/components/Icon';
import { Speciality } from '../../../../../../base/interfaces/config';
import { vacancyFormActions } from '../../../redux/slices/vacancyFormSlice';
import { highlightMatchingText } from '../../../../../../base/utils';
import { VACANCY_MAX_SPECIALITY_COUNT } from '../../../constants';
import { appActions } from '../../../../../../base/redux/slices/appSlice';
import { Loading } from '../../../../../../base/enums';

import './styles.scss';
import { employerAppActions } from '../../../../../redux/slices/employerAppSlice';

export const SpecialityAutocompleteDesktop = () => {
    const dispatch = useAppDispatch();
    const { loading } = useAppSelector((state) => state.app);
    const {
        fields: { specialities: selectedOptions, areas },
        errors,
    } = useAppSelector((state) => state.vacancyForm);
    const options = useAppSelector((state) => state.employerApp.specialitySearchOptions);

    const [inputValue, setInputValue] = useState('');
    const [optionsSelectVisible, setOptionsSelectVisible] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);

    const isLoading = useMemo(() => loading === Loading.SpecialityAutocomplete, [loading]);

    const inputChangeHandler = (e: ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.trimStart();
        dispatch(employerAppActions.specialitySearchSuccess(null));
        setInputValue(value);
        setOptionsSelectVisible(true);
        if (value) {
            dispatch(employerAppActions.specialitySearchRequest(value));
        }
    };

    const onInputFocus = () => {
        setShowOverlay(true);
    };

    const optionSelectHandler = (option: Speciality) => {
        dispatch(
            vacancyFormActions.setField({
                id: 'specialities',
                value: [...selectedOptions, option],
                errorGroupName: 'specialityIds',
            }),
        );
        dispatch(
            vacancyFormActions.setField({
                id: 'areas',
                value: [...areas, option.area],
            }),
        );
        dispatch(employerAppActions.specialitySearchSuccess(null));
        setInputValue('');
        setOptionsSelectVisible(false);
        setShowOverlay(false);
    };

    const onOverlayClick = () => {
        setOptionsSelectVisible(false);
        setShowOverlay(false);
        setInputValue('');
        dispatch(employerAppActions.specialitySearchSuccess(null));
        dispatch(appActions.setLoading(null));
    };

    const onChipDelete = (id: number) => {
        const index = selectedOptions.findIndex((option) => option.id === id);
        const filteredSpecialities = selectedOptions.filter((option) => option.id !== id);
        dispatch(
            vacancyFormActions.setField({
                id: 'specialities',
                value: filteredSpecialities,
                errorGroupName: 'specialityIds',
            }),
        );
        dispatch(
            vacancyFormActions.setField({
                id: 'areas',
                value: areas.filter((v, i) => i !== index),
            }),
        );
    };

    const setInputFocus = () => {
        if (inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const loader = useMemo(
        () => (isLoading ? <CircularProgress size={16} className='app-speciality-autocomplete__loader' /> : null),
        [isLoading],
    );

    const renderOptions = useMemo(() => {
        if (!optionsSelectVisible || !options) {
            return null;
        }

        if (options && !options.length && !isLoading) {
            return (
                <ul className='app-speciality-autocomplete__options-list'>
                    <li className='app-speciality-autocomplete__option-item'>
                        <span>{__t('По вашему запросу ничего не найдено')}</span>
                    </li>
                </ul>
            );
        }

        let optionsToRender = options;
        if (selectedOptions.length) {
            const selectedOptionsIds = selectedOptions.map((s) => s.id);
            optionsToRender = options.filter((i) => !selectedOptionsIds.includes(i.id));
        }

        return (
            <ul className='app-speciality-autocomplete__options-list'>
                {optionsToRender.map((option) => (
                    <li
                        key={option.id}
                        className='app-speciality-autocomplete__option-item'
                        onClick={() => optionSelectHandler(option)}
                    >
                        <span className='app-speciality-autocomplete__option-title'>
                            {highlightMatchingText(option.name, inputValue)}
                        </span>
                        <span className='app-speciality-autocomplete__option-additional-text'>{option.area.name}</span>
                    </li>
                ))}
            </ul>
        );
    }, [options, selectedOptions, optionsSelectVisible, inputValue, isLoading]);

    const renderOverlay = useMemo(() => {
        if (showOverlay) {
            return <div className='app-speciality-autocomplete__overlay' onClick={onOverlayClick}></div>;
        }
    }, [showOverlay]);

    const renderInput = useMemo(() => {
        if (selectedOptions.length >= VACANCY_MAX_SPECIALITY_COUNT) {
            return null;
        }

        return (
            <input
                ref={inputRef}
                type='text'
                value={inputValue}
                onChange={inputChangeHandler}
                onFocus={onInputFocus}
                className='app-speciality-autocomplete__input-component'
                placeholder={!selectedOptions.length ? __t('К примеру, «Работник склада»') : ''}
                autoComplete='off'
            />
        );
    }, [inputRef, inputValue, selectedOptions, VACANCY_MAX_SPECIALITY_COUNT, inputChangeHandler, onInputFocus]);

    const renderSelectedChips = useMemo(
        () =>
            selectedOptions.map((option) => (
                <div className='selected-speciality-chip' key={option.id}>
                    <span className='selected-speciality-chip__value'>{option.name}</span>
                    <div className='selected-speciality-chip__remove-button' onClick={() => onChipDelete(option.id)}>
                        <Icon icon='xmark-regular' size='10' />
                    </div>
                </div>
            )),
        [selectedOptions],
    );

    return (
        <>
            <div className='app-speciality-autocomplete'>
                <div className='app-speciality-autocomplete__input-container' onClick={setInputFocus}>
                    {renderSelectedChips}
                    {renderInput}
                    {loader}
                </div>
                {renderOptions}
                {renderOverlay}
            </div>

            <div className='app-speciality-autocomplete__additional-data-wrap'>
                <ErrorMessage errors={errors?.specialityIds} />
                <div className='app-speciality-autocomplete__speciality-count'>
                    {selectedOptions.length}/{VACANCY_MAX_SPECIALITY_COUNT}
                </div>
            </div>
        </>
    );
};
